import { Control, FieldErrors, Controller } from "react-hook-form";
import { MultiSelectAutocomplete } from "@/components/MultiSelectAutocomplete";
import { CustomSelect } from "@/components";
import { UserV2FormValues } from "./UserFormV2";
import { useTagsV2 } from "@/hooks/useTagsV2";
import { MC_FEATURE_NAME, TAG_TYPE_NAME } from "@/types/tagsV2";

interface MarketCanvasFormPartProps {
  control: Control<UserV2FormValues>;
  errors: FieldErrors<UserV2FormValues>;
  index: number;
  currentCbsaTags: number[];
  currentConfidentialityLevel: number;
  currentAccessableFeatures: number[];
  handleCbsaTagsChange: (selectedCbsaTags: number[]) => void;
  handleAccessebleFeaturesChange: (selectedFeatures: number[]) => void;
  handleConfidentialityLevelChange: (confidentialityLevel: number) => void;
}

export const MarketCanvasFormPart = ({
  control,
  errors,
  index,
  currentCbsaTags,
  currentConfidentialityLevel,
  handleCbsaTagsChange,
  handleConfidentialityLevelChange,
  currentAccessableFeatures,
  handleAccessebleFeaturesChange,
}: MarketCanvasFormPartProps) => {
  const { tagOptions: cbsaOptions, loadingTags: loadingCbsaTags } = useTagsV2(
    TAG_TYPE_NAME.CBSA,
  );
  const {
    tagOptions: confidentialityLevelOptions,
    loadingTags: loadingConfidentialityLevelTags,
  } = useTagsV2(TAG_TYPE_NAME.CONFIDENTIALITY_LEVEL);

  const { tagOptions: availableFeatures, loadingTags: isFeaturesLoading } =
    useTagsV2(TAG_TYPE_NAME.MC_FEATURES_ACCESS);

  const formatNameFromDb = (dbName: string) => {
    if (dbName === MC_FEATURE_NAME.ADVANCED_POLYGON) {
      return "Advanced Polygon";
    }

    return dbName
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const formattedLabelFromDb = availableFeatures.map((feature) => ({
    label: formatNameFromDb(feature.label),
    value: feature.value,
  }));

  return (
    <>
      <Controller
        name={`roles.${index}.cbsaTags` as const}
        control={control}
        render={() => (
          <MultiSelectAutocomplete
            options={cbsaOptions}
            selected={currentCbsaTags}
            setSelected={handleCbsaTagsChange}
            label="Select Regions*"
            placeholder="Select From List"
            maxHeight={300}
            loading={loadingCbsaTags}
          />
        )}
      />
      <CustomSelect
        name={`roles.${index}.confidentiality`}
        label="Confidentiality Level*"
        placeholder="Select Confidentiality Level"
        options={confidentialityLevelOptions}
        value={currentConfidentialityLevel}
        onChange={handleConfidentialityLevelChange}
        error={!!errors.roles?.[index]?.confidentialityLevel}
        helperText={errors.roles?.[index]?.confidentialityLevel?.message}
        loading={loadingConfidentialityLevelTags}
      />
      <Controller
        name={`roles.${index}.accessFeaturesTags` as const}
        control={control}
        render={() => (
          <MultiSelectAutocomplete
            options={formattedLabelFromDb}
            selected={currentAccessableFeatures}
            setSelected={handleAccessebleFeaturesChange}
            label="Select Additional Features"
            placeholder="Select From List"
            maxHeight={300}
            loading={isFeaturesLoading}
          />
        )}
      />
    </>
  );
};
